import { Horizontal, ManyTag, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ICellRendererParams } from 'ag-grid-community'
import { Popover, type RefSelectProps, Tooltip } from 'antd'
import { EbolMappingButton } from '../EbolMappingButton'
import { type PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import { compareCell, type ComparisonArgs } from './ComparisonCell'
import { CellContainer } from './CellContainer'

const WithTooltipEvents = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />
const SourceObjectLine = ({ sourceSystem, sourceId }: { sourceSystem: string; sourceId: string }) => (
  <>
    <Tooltip title='Supply Map: source system | source id'>
      <WithTooltipEvents>
        <Vertical
          horizontalCenter
          verticalCenter
          border='all'
          borderRadius='4px'
          flex='0'
          style={{
            borderColor: 'var(--theme-color-1)',
            width: '12px',
            height: '12px',
            flexShrink: 0,
            flexGrow: 1,
          }}
        >
          <Texto align='center' appearance='primary' style={{ fontSize: '8px', width: '12px' }}>
            E
          </Texto>
        </Vertical>
      </WithTooltipEvents>
    </Tooltip>
    <Texto appearance='hint'>{`${sourceSystem} | ${sourceId}`}</Texto>
  </>
)
const SourceLineWrapper = ({ enabled, children }: PropsWithChildren<{ enabled?: boolean }>) => (
  <Horizontal
    style={{
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: '2px',
      marginBottom: enabled ? '-4px' : undefined,
    }}
  >
    {children}
  </Horizontal>
)

type SourceObjectValueProps = {
  items?: string[]
  placeholder?: string
  itemDisplayLimit?: number
}
export const SourceObjectValue = ({
  items,
  placeholder = 'Unmapped',
  itemDisplayLimit = 1,
}: SourceObjectValueProps) => (
  <Vertical style={{ flexGrow: 1 }}>
    {items?.length ? <ManyTag tagItems={items} maxCount={itemDisplayLimit} /> : <Texto>{placeholder}</Texto>}
  </Vertical>
)

export type SourceObjectDropdownProps = Pick<SourceObjectCellProps, 'value' | 'sourceSystem'>
export type SourceObjectDropdown = React.ForwardRefExoticComponent<
  SourceObjectDropdownProps & React.RefAttributes<RefSelectProps>
>

export type SourceObjectCellProps<TData = Gravitate.Ebol> = Omit<ComparisonArgs<string>, 'value'> & {
  showSource?: boolean
  sourceSystem: string
  preventAdding?: boolean
  itemDisplayLimit?: number
  dropdown?: React.FC<Pick<SourceObjectCellProps, 'value' | 'sourceSystem'> & React.RefAttributes<RefSelectProps>>
} & ICellRendererParams<TData, Gravitate.SourceObject, unknown>
export function SourceObjectCell<T>({
  value,
  previousValue,
  sourceSystem,
  isEqual,
  preventAdding,
  showSource,
  itemDisplayLimit,
  dropdown: Dropdown,
}: SourceObjectCellProps<T>) {
  const match = useMemo(
    () => !!value?.id_names?.some((x) => compareCell({ value: x.name, previousValue, isEqual })),
    [value, previousValue, isEqual]
  )
  const status = useMemo(() => (match ? 'success' : value?.id_names?.length ? 'default' : 'error'), [match, value])
  const itemNames = useMemo(() => value?.id_names?.map((x) => x.name)?.reverse(), [value])
  const [isEditing, setIsEditing] = useState(false)
  const dropdownRef = useRef<RefSelectProps>(null)

  useEffect(() => {
    //todo: for some reason this isn't able to regain focus after the first focus grab. investigate
    if (isEditing) dropdownRef.current?.focus?.()
  }, [isEditing])

  const showingSource = showSource && !!value?.source_id
  return (
    <CellContainer status={status}>
      {showingSource && (
        <SourceLineWrapper enabled>
          <SourceObjectLine sourceSystem={sourceSystem} sourceId={value?.source_id} />
        </SourceLineWrapper>
      )}
      {preventAdding ? (
        <SourceLineWrapper enabled={showingSource}>
          {showSource && <div style={{ width: '12px' }} />}
          <SourceObjectValue itemDisplayLimit={itemDisplayLimit} items={itemNames} placeholder={value?.source_id} />
        </SourceLineWrapper>
      ) : (
        <Popover
          trigger='click'
          placement='bottomLeft'
          open={isEditing}
          onOpenChange={setIsEditing}
          arrow={false}
          content={() =>
            Dropdown === undefined ? null : <Dropdown ref={dropdownRef} value={value} sourceSystem={sourceSystem} />
          }
        >
          <WithTooltipEvents style={{ marginBottom: showingSource ? '-4px' : undefined, padding: '5px 0px' }}>
            <EbolMappingButton editing={isEditing}>
              <SourceObjectValue itemDisplayLimit={itemDisplayLimit} items={itemNames} placeholder={value?.source_id} />
            </EbolMappingButton>
          </WithTooltipEvents>
        </Popover>
      )}
    </CellContainer>
  )
}
